import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FormCheckbox } from "eone-ui/forms/form-checkbox";
import { FormSpacer } from "eone-ui/forms/form-spacer";
import { FormSelect } from "eone-ui/forms/form-select";
import { FormPanel } from "eone-ui/panels/form-panel";
import { Navigation } from "eone-ui/navigation/navigation";
import { data_get } from "popdock-shared/Functions/data-access";
import { ConnectComponent } from "popdock-shared/connect-v2/connect-component.jsx";

// data_get never throws: a failed request resolves to { error } (or { status, error } for a non-2xx).
const isFailed = (result) => !result || !!result.error;

function AddConnector(props) {
    const { id: connectorSetupId } = useParams();
    const navigate = useNavigate();

    const [connectorSetup, setConnectorSetup] = useState(null);
    const [auths, setAuths] = useState([]);
    const [selectedAuthId, setSelectedAuthId] = useState(null);
    const [connectorAuth, setConnectorAuth] = useState(null);
    const [multipleCredentials, setMultipleCredentials] = useState(false);
    const [loadingAuths, setLoadingAuths] = useState(true);
    const [loadingAuth, setLoadingAuth] = useState(true);
    const [loadError, setLoadError] = useState(null);
    // Only the latest auth request may land — switching auths quickly must not leave one auth's
    // fields showing under another auth's selection.
    const authRequestRef = useRef(0);

    useEffect(() => {
        // Moving to the next connector reuses this component, so start each step clean.
        setConnectorSetup(null);
        setAuths([]);
        setSelectedAuthId(null);
        setConnectorAuth(null);
        setLoadError(null);
        setLoadingAuths(true);
        setLoadingAuth(true);

        // Coming back from the OAuth round-trip: the callback names the auth that was used and the
        // multiple-credentials choice made before leaving. Read them now — ConnectComponent scrubs
        // the round-trip params from the address bar once it mounts. (It also reads and shows any
        // authError itself, so the page doesn't.)
        const urlParams = new URLSearchParams(window.location.search);
        const returnedAuthId = urlParams.get("connectorAuthId");
        setMultipleCredentials(urlParams.get("multipleCredentials") === "true");

        loadConnectorSetup(returnedAuthId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [connectorSetupId]);

    const loadConnectorSetup = async (returnedAuthId) => {
        const setup = await data_get(`/AddConnector/ConnectorSetup/${connectorSetupId}`);
        if (isFailed(setup)) {
            setLoadError("This connector could not be loaded. Please restart the install.");
            setLoadingAuths(false);
            return;
        }
        setConnectorSetup(setup);
        if (props.SetConnectorSetupId) props.SetConnectorSetupId(connectorSetupId);
        await loadAuths(returnedAuthId);
    };

    const loadAuths = async (returnedAuthId) => {
        const response = await data_get(`/connect-v2/auths/${connectorSetupId}`);
        if (isFailed(response)) {
            setLoadError("The authentication methods for this connector could not be loaded. Please try again.");
            setLoadingAuths(false);
            return;
        }
        const { defaultAuthId, connectorAuths = [] } = response;
        setAuths(connectorAuths);
        if (connectorAuths.length > 0) {
            const listed = (id) => id && connectorAuths.find(a => a.ConnectorAuthId?.toLowerCase() === id.toLowerCase());
            const initialAuthId = (listed(returnedAuthId) || listed(defaultAuthId) || connectorAuths[0]).ConnectorAuthId;
            setSelectedAuthId(initialAuthId);
            loadAuth(initialAuthId);
        }
        setLoadingAuths(false);
    };

    const loadAuth = async (authId) => {
        const request = ++authRequestRef.current;
        setLoadingAuth(true);
        const auth = await data_get(`/connect-v2/auth/${authId}?connectorSetupId=${connectorSetupId}`);
        if (request !== authRequestRef.current) return;
        if (isFailed(auth)) {
            setConnectorAuth(null);
            setLoadError("This authentication method could not be loaded. Please try again.");
        } else {
            setConnectorAuth(auth);
            setLoadError(null);
        }
        setLoadingAuth(false);
    };

    const changeAuthOption = (newAuthId) => {
        if (selectedAuthId !== newAuthId) {
            setSelectedAuthId(newAuthId);
            loadAuth(newAuthId);
        }
    };

    // The backend builds AuthorizeUri without the multiple-credentials choice; carry it through the
    // OAuth round-trip so the callback can save it (the same as Popdock.Web's install flow).
    const flowConnectorAuth = useMemo(() => {
        if (!connectorAuth?.AuthorizeUri) return connectorAuth;
        const sep = connectorAuth.AuthorizeUri.includes("?") ? "&" : "?";
        return { ...connectorAuth, AuthorizeUri: `${connectorAuth.AuthorizeUri}${sep}multipleCredentials=${multipleCredentials}` };
    }, [connectorAuth, multipleCredentials]);

    // The connect flow treats a throw as "session expired"; data_get resolves failures instead.
    const getUserValues = async (id) => {
        const values = await data_get(`/connect-v2/user-values/${id}`);
        if (isFailed(values)) throw new Error("User values not found");
        return values;
    };

    const handleConnected = (connectResult) => {
        navigate(connectResult?.RedirectTo || "/ShowWidgetIds");
    };

    const getAuthOptions = () =>
        auths.map(auth => ({ Value: auth.Name, Key: auth.ConnectorAuthId }));

    const hasMultipleAuthOptions = () =>
        auths.find(a => a.ConnectorAuthId === selectedAuthId)?.MultipleUser === true;

    const connectorName = connectorSetup?.Name || "";

    const getConnectUrl = () =>
        `/connect-v2/${selectedAuthId}?connectorSetupId=${connectorSetupId}&name=${encodeURIComponent(connectorName)}&multipleCredentials=${multipleCredentials}`;

    const getConnectWithSettingsUrl = () =>
        `/connect-v2/${selectedAuthId}/connect-with-settings?connectorSetupId=${connectorSetupId}&name=${encodeURIComponent(connectorName)}&multipleCredentials=${multipleCredentials}`;

    const getValidateUrl = () =>
        `/connect-v2/${selectedAuthId}/validate?connectorSetupId=${connectorSetupId}`;

    const getSettingsMetadataUrl = () =>
        `/connect-v2/${selectedAuthId}/settings-metadata?connectorSetupId=${connectorSetupId}`;

    return (
        <Navigation ItemsLabel={"Steps"} Items={props.NavigationItems} Selected={connectorSetupId?.toLowerCase()}>
            <FormSpacer />
            {loadError && (
                <FormPanel Title="Something went wrong">
                    <div>{loadError}</div>
                </FormPanel>
            )}
            {loadingAuths && (
                <FormPanel Title="Loading">
                    <div>Loading connector authentication options...</div>
                </FormPanel>
            )}
            {!loadingAuths && !loadError && auths.length === 0 && (
                <FormPanel Title="No Authentication Methods">
                    <div>No authentication methods are available for this connector.</div>
                </FormPanel>
            )}
            {!loadingAuths && auths.length > 0 && (
                <FormPanel Title={connectorSetup?.Name || "Connect"}>
                    <FormSelect
                        Label="Authentication Method"
                        Visible={auths.length > 1}
                        Value={selectedAuthId}
                        Options={getAuthOptions()}
                        OnChange={changeAuthOption}
                    />
                    {hasMultipleAuthOptions() && (
                        <>
                            <FormCheckbox
                                Label="Allow multiple users to connect with their own credentials"
                                Value={multipleCredentials}
                                OnToggle={() => setMultipleCredentials(!multipleCredentials)}
                            />
                            <FormSpacer />
                        </>
                    )}
                    {!loadingAuth && flowConnectorAuth && (
                        <ConnectComponent
                            ConnectorAuth={flowConnectorAuth}
                            ConnectUrl={getConnectUrl()}
                            ConnectWithSettingsUrl={getConnectWithSettingsUrl()}
                            ValidateUrl={getValidateUrl()}
                            SettingsMetadataUrl={getSettingsMetadataUrl()}
                            GetUserValues={getUserValues}
                            OnConnected={handleConnected}
                            OnAuthenticated={() => { }}
                            OnAuthenticatedSettingsChanged={() => { }}
                            Visible={true}
                        />
                    )}
                    {loadingAuth && (
                        <div>Loading authentication settings...</div>
                    )}
                </FormPanel>
            )}
        </Navigation>
    );
}

export { AddConnector };
